import { useState } from 'react';

const CATEGORY_COLORS = {
  sensory: 'bg-pink-100 text-pink-700',
  board_games: 'bg-indigo-100 text-indigo-700',
  card_games: 'bg-indigo-100 text-indigo-700',
  outdoor: 'bg-green-100 text-green-700',
  stem: 'bg-cyan-100 text-cyan-700',
  action_figures: 'bg-red-100 text-red-700',
  pretend_play: 'bg-orange-100 text-orange-700',
  arts_crafts: 'bg-purple-100 text-purple-700',
  construction: 'bg-yellow-100 text-yellow-700',
  dolls: 'bg-rose-100 text-rose-700',
  electronic: 'bg-blue-100 text-blue-700',
  plush: 'bg-amber-100 text-amber-700',
  educational: 'bg-teal-100 text-teal-700',
  puzzles: 'bg-violet-100 text-violet-700',
  vehicles: 'bg-slate-100 text-slate-700',
};

const PLATFORM_STYLES = {
  tiktok: { label: 'TT', bg: '#000000', color: '#FFFFFF' },
  instagram: { label: 'IG', bg: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#FFFFFF' },
  youtube: { label: 'YT', bg: '#FF0000', color: '#FFFFFF' },
  twitter: { label: 'X', bg: '#000000', color: '#FFFFFF' },
};

export default function TrendCard({ trend }) {
  const [showSources, setShowSources] = useState(false);
  const categoryStyle = CATEGORY_COLORS[trend.trend_category] || 'bg-gray-100 text-gray-600';
  const daysAgo = Math.floor((Date.now() - new Date(trend.detected_at).getTime()) / (1000 * 60 * 60 * 24));
  const sources = trend.source_urls || [];

  return (
    <div className="bg-white rounded-xl border border-vault-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg text-vault-navy">{trend.trend_topic}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${categoryStyle}`}>
              {trend.trend_category?.replace(/_/g, ' ')}
            </span>
            <span className="text-xs text-gray-400">
              {daysAgo === 0 ? 'Today' : `${daysAgo}d ago`}
            </span>
          </div>
        </div>

        {/* Platform tags — app-colored */}
        <div className="flex gap-1">
          {trend.trend_platforms?.map(p => {
            const style = PLATFORM_STYLES[p];
            if (!style) return (
              <span key={p} className="text-xs px-1.5 py-0.5 bg-vault-surface rounded text-gray-500 font-medium">{p}</span>
            );
            return (
              <span
                key={p}
                className="text-xs px-1.5 py-0.5 rounded font-bold"
                style={{ background: style.bg, color: style.color }}
              >
                {style.label}
              </span>
            );
          })}
        </div>
      </div>

      <p className="text-[15px] text-gray-600 leading-relaxed tracking-wide">{trend.trend_summary}</p>

      {/* Sources dropdown */}
      {sources.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowSources(!showSources)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-vault-gold transition-colors"
          >
            <svg className={`w-3 h-3 transition-transform ${showSources ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Sources ({sources.length})</span>
          </button>

          {showSources && (
            <div className="mt-2 space-y-2 pl-4 border-l-2 border-vault-border">
              {sources.map((src, i) => (
                <div key={i} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-vault-navy">{src.name}</span>
                    {src.date && <span className="text-gray-400">{src.date}</span>}
                    {src.views && <span className="text-gray-400">{src.views} views</span>}
                  </div>
                  {src.url && (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-vault-gold hover:underline break-all"
                    >
                      {src.url.length > 60 ? src.url.slice(0, 60) + '...' : src.url}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
